// Файл consent_panel.js

/**
 * Модуль «consent panel» содержит логику раздела Consent Panel.
 * Он объединяет связанные обработчики, данные и элементы интерфейса.
 * Такое разделение упрощает сопровождение и повторное использование кода.
 */

import { useEffect, useState } from 'react';

import DriverPanel from '../DriverPanel';
import DocumentAttachments from '../DocumentAttachments';
import Button from '../Button/Button';

import {
    download_policy_file,
    load_policy_file_blob,
} from './policy_file_actions';
import {
    POLICY_VERSION,
    is_consent_still_valid,
    is_notice_still_recent,
    read_consent,
    save_consent,
    save_notice_shown,
} from './consent_panel_storage';



const COOKIE_STORAGE_KEY = 'cookie_consent_v1';
const PD_STORAGE_KEY = 'pd_consent_v1';


const PRESETS = {
    cookies: {
        title: 'Использование cookies',
        storage_key: COOKIE_STORAGE_KEY,
        log_table: 'log_cookie_consent',
        attachment_table: 'get_policy_attachment',
        attachment_id: 'cookie_policy',
        intro_text: (
            'Сайт использует файлы cookies и локальное хранилище '
            + 'браузера исключительно для работы авторизации, '
            + 'сохранения сессии и фиксации факта согласия. '
            + 'Технические cookies необходимы для работы сайта и '
            + 'не могут быть отключены.'
        ),
        notice_text: (
            'Опциональные cookies устанавливаются только с вашего '
            + 'согласия. Согласие можно отозвать в любой момент.'
        ),
        empty_text: 'Политика временно недоступна',
        error_text: 'Не удалось загрузить политику',
    },
    pd_policy: {
        title: 'Обработка персональных данных',
        storage_key: PD_STORAGE_KEY,
        log_table: 'log_pd_consent',
        attachment_table: 'get_policy_attachment',
        attachment_id: 'pd_policy',
        intro_text: (
            'Ознакомьтесь с Политикой обработки персональных '
            + 'данных Оператора. Согласие на обработку '
            + 'персональных данных даётся в форме входа в '
            + 'личный кабинет и является добровольным, '
            + 'конкретным, информированным и сознательным '
            + '(ст. 9 Федерального закона № 152-ФЗ).'
        ),
        notice_text: (
            'Вы вправе в любой момент отозвать согласие на '
            + 'обработку персональных данных, нажав кнопку '
            + '«Отозвать согласие».'
        ),
        empty_text: 'Политика временно недоступна',
        error_text: 'Не удалось загрузить политику',
    },
};


const format_date_ru = (iso_string) => {
    try {
        const d = new Date(iso_string);

        if (Number.isNaN(d.getTime()))
            return '';

        return d.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    catch {
        return '';
    }
};


const ConsentPanel = ({
    mode = 'cookies',
    triggerMode = 'auto',
    isOpen: isOpenProp,
    onClose,
    showAcceptButtons = true,
}) => {
    const preset = PRESETS[mode] || PRESETS.cookies;

    const [isOpenInternal, setIsOpenInternal] = useState(false);
    const [savedConsent, setSavedConsent] = useState(
        () => read_consent(preset.storage_key));

    const isControlled = triggerMode === 'manual';
    const isOpen = isControlled ? !!isOpenProp : isOpenInternal;
    const [has_opened, set_has_opened] = useState(isOpen);
    const should_render_content = has_opened || isOpen;

    useEffect(() => {
        if (isOpen) {
            set_has_opened(true);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isControlled) {
            return;
        }

        if (is_consent_still_valid(preset.storage_key)) {
            return;
        }

        if (mode === 'cookies' && is_notice_still_recent()) {
            return;
        }

        if (mode === 'cookies') {
            save_notice_shown();
        }

        setIsOpenInternal(true);
    }, [isControlled, mode, preset.storage_key]);

    useEffect(() => {
        if (isControlled && isOpenProp)
            setSavedConsent(read_consent(preset.storage_key));
    }, [isControlled, isOpenProp, preset.storage_key]);

    const close_panel = () => {
        if (isControlled)
            onClose && onClose();

        else
            setIsOpenInternal(false);
    };

    const handle_accept = () => {
        save_consent(preset, 'accepted');
        setSavedConsent(
            read_consent(preset.storage_key));
        close_panel();
    };

    const handle_decline = () => {
        save_consent(preset, 'declined');
        setSavedConsent(read_consent(preset.storage_key));
        close_panel();
    };

    const already_accepted =
        savedConsent
        && savedConsent.consent === 'accepted'
        && savedConsent.version === POLICY_VERSION;

    return (
        <DriverPanel
            isOpen={isOpen}
            title={preset.title}
            onClose={close_panel}
            scrollable={true}
        >
            {should_render_content && (
                <>
                    <div className="block_1">
                        {preset.intro_text}
                    </div>

                    <div className="block_1">
                        <DocumentAttachments
                            ID_document={preset.attachment_id}
                            tableName={preset.attachment_table}
                            emptyText={preset.empty_text}
                            errorText={preset.error_text}
                            loadFileBlob={(file_name) => (
                                load_policy_file_blob(
                                    preset.attachment_table,
                                    preset.attachment_id,
                                    file_name,
                                )
                            )}
                            downloadFile={(file_name) => (
                                download_policy_file(
                                    preset.attachment_table,
                                    preset.attachment_id,
                                    file_name,
                                )
                            )}
                        />
                    </div>

                    <div className="block_1">
                        {preset.notice_text}
                    </div>

                    {already_accepted && (
                        <div className="block_1">
                            Вы дали согласие
                            {' '}{format_date_ru(savedConsent.acceptedAt)}
                            {' '}(версия политики {savedConsent.version}).
                        </div>
                    )}

                    {already_accepted && (
                        <div className="block_1">
                            <Button
                                text="Отозвать согласие"
                                variant="secondary"
                                clickAction={handle_decline}
                            />
                        </div>
                    )}

                    {!already_accepted && showAcceptButtons && (
                        <div className="block_1">
                            <Button
                                text="Отклонить"
                                variant="secondary"
                                clickAction={handle_decline}
                            />
                            <Button
                                text="Принять"
                                variant="primary"
                                clickAction={handle_accept}
                            />
                        </div>
                    )}
                </>
            )}
        </DriverPanel>
    );
};


export default ConsentPanel;
